import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  FacebookButtonPlain,
  FacebookLink,
  InstagramButtonPlain,
  InstagramLink,
  TripAdvisorLink,
} from './SocialButton';
import delay from 'lodash-es/delay';
import { H1 } from '../typography';
import LangButton from '../i18n/LangButton';
import { languageOptions } from '../i18n/languages';
import { LanguageContext } from '../i18n/LanguageContext';

const ShareContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  > * {
    margin-top: 0.8em;
    pointer-events: all;
  }
  font-size: 0.6em;
  opacity: 0.8;
  width: 100%;
  align-items: center;
`;

const variants = {
  closed: {
    rotateZ: 0,
    translateX: 0,
    flexDirection: 'column',
    transition: {
      rotateZ: { stiffness: 1000 },
      delay: 0.5,
    },
  },
  open: {
    rotateZ: 90,
    translateX: '200%',
    transition: {
      rotateZ: { stiffness: 1000 },
    },
    width: '100%',
    justifyContent: 'space-between',
  },
};

const fanVariants = {
  fanOut: {
    opacity: 1,
    left: '5em',
    transform: 'scale(1) translateY(-25%)',
    pointerEvents: 'all',
  },
  fanIn: {
    opacity: 0,
    left: '-2em',
    transform: 'scale(0.2) translateY(-55%)',
    pointerEvents: 'none',
  },
};

const Menu = styled.div`
  position: relative;
`;

const MenuItems = styled(motion.div)`
  opacity: 0;
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  flex-direction: column;

  > * {
    font-size: 0.3em;
    color: black;
  }
  * + * {
    margin-top: 0.5em;
  }
`;

const MenuItem = styled.a`
  display: flex;
  justify-content: start;
  align-items: center;

  background: rgba(255, 255, 255, 0.7);
  font-size: 0.9em;
  cursor: pointer;
  padding: 0.3em;
  border-radius: 1em;

  :hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

const ExplodingMenu = ({
  id,
  children,
  onActivate,
  onSelect,
  enable,
  items,
  Component,
}) => {
  const [active, setActive] = useState(false);
  const [timer, setTimer] = useState(false);
  const onEnter = () => {
    setActive(true);
    onActivate(id);
    clearTimeout(timer);
    setTimer(null);
  };

  const onLeave = () => {
    setTimer(delay(setActive, 300, false));
  };

  return (
    <Menu onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {children}
      <MenuItems
        variants={fanVariants}
        animate={active && enable ? 'fanOut' : 'fanIn'}
      >
        {items.map((item, i) => (
          <MenuItem
            key={i}
            {...item}
            onClick={() => (onSelect ? onSelect(item.lang) : undefined)}
          >
            <Component {...item} onSelect={onSelect} />
            <H1 style={{ margin: 0, fontSize: '1rem', marginLeft: '0.5em' }}>
              {item.label}
            </H1>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};

const SocialShare = ({ style, isOpen }) => {
  const [current, setCurrent] = useState(undefined);
  const cycleLang = current => {
    const idx = languageOptions.findIndex(_ => _.lang === current);
    return languageOptions[(idx + 1) % languageOptions.length].lang;
  };

  return (
    <ShareContainer variants={variants} style={style}>
      <LanguageContext.Consumer>
        {({ language, changeLanguage }) => (
          <ExplodingMenu
            id={'lang'}
            enable={current === 'lang' && !isOpen}
            Component={LangButton}
            onActivate={setCurrent}
            onSelect={changeLanguage}
            items={languageOptions.filter(_ => _.lang !== language)}
          >
            <LangButton
              lang={language}
              onClick={
                isOpen ? () => changeLanguage(cycleLang(language)) : undefined
              }
            />
          </ExplodingMenu>
        )}
      </LanguageContext.Consumer>
      <ExplodingMenu
        enable={current === 'facebook' && !isOpen}
        id={'facebook'}
        onActivate={setCurrent}
        Component={FacebookButtonPlain}
        items={[
          {
            href: 'https://www.facebook.com/LaVillaBudva/',
            label: 'LaVilla',
            target: '_blank',
          },
          {
            href: 'https://www.facebook.com/ShanghaiBudva/',
            label: 'Shanghai',
            target: '_blank',
          },
        ]}
      >
        <FacebookLink
          href={isOpen ? 'https://www.facebook.com/LaVillaBudva/' : undefined}
        />
      </ExplodingMenu>
      <ExplodingMenu
        enable={current === 'instagram' && !isOpen}
        id={'instagram'}
        onActivate={setCurrent}
        Component={InstagramButtonPlain}
        items={[
          {
            href: 'https://www.instagram.com/lavilla.budva/',
            label: 'LaVilla',
            target: '_blank',
          },
          {
            href: 'https://www.instagram.com/_shanghai_budva_/',
            label: 'Shanghai',
            target: '_blank',
          },
        ]}
      >
        <InstagramLink
          href={isOpen ? 'https://www.instagram.com/lavilla.budva/' : undefined}
        />
      </ExplodingMenu>
      <TripAdvisorLink
        to={
          'https://www.tripadvisor.com/Hotel_Review-g304074-d17691693-Reviews-La_Villa_Boutique_Hotel-Budva_Budva_Municipality.html'
        }
        style={{ fontSize: '0.65rem' }}
      />
    </ShareContainer>
  );
};

export default SocialShare;